/** @domain address -- Physical address components: street, city, state, zip, country. */
import { z } from 'zod';
import { createDomainField } from './types';
import { capitalizeFirst, maskZipCode, trimExtraSpaces } from './formatters';
import { maxCharMessage } from '../validation/messages';

const define = createDomainField('address');

export const addressLine1 = define({
  label: 'Address Line 1',
  schema: z
    .string()
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank')
    .refine((v) => !v.includes('  '), 'Must not contain double spaces'),
  adapterHint: 'text',
  controlWidth: 'full',
  formatter: trimExtraSpaces,
  displayFormat: 'text',
});

export const addressLine2 = define({
  label: 'Address Line 2',
  schema: z
    .string()
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank')
    .refine((v) => !v.includes('  '), 'Must not contain double spaces'),
  adapterHint: 'text',
  controlWidth: 'full',
  formatter: trimExtraSpaces,
  displayFormat: 'text',
});

export const city = define({
  label: 'City',
  schema: z.string().max(100, 'Must be at most 100 characters'),
  adapterHint: 'text',
  formatter: [trimExtraSpaces, capitalizeFirst],
  displayFormat: 'text',
});

export const state = define({
  label: 'State/Province/Region',
  schema: z.string(),
  adapterHint: 'async-select',
  controlWidth: 'short',
  displayFormat: 'text',
});

export const stateText = define({
  label: 'State / Province',
  placeholder: 'Enter state or province',
  schema: z.string().max(100, 'Must be at most 100 characters'),
  adapterHint: 'text',
  controlWidth: 'short',
  formatter: [trimExtraSpaces, capitalizeFirst],
  displayFormat: 'text',
});

/**
 * Base postal code — max 20 chars, free-form (letters/spaces/dashes allowed).
 * US "exactly 5 digits" is enforced at the form-block layer via FormRule +
 * `when.formatter: maskZipCode` when `isOtherCountry` is false (legacy parity).
 */
export const zipCode = define({
  label: 'ZIP/Postal Code',
  schema: z.string().max(20, maxCharMessage(20)),
  adapterHint: 'text',
  controlWidth: 'short',
  formatter: maskZipCode,
  displayFormat: 'text',
});

export const country = define({
  label: 'Country',
  placeholder: 'Select',
  schema: z.string(),
  adapterHint: 'async-select',
  displayFormat: 'text',
});

export const countryOfLegalResidency = define({
  label: 'Country of Legal Residency',
  placeholder: 'Select',
  schema: z.string(),
  adapterHint: 'async-select',
  displayFormat: 'text',
});

export const currentHomeAddress = define({
  label: 'Current Home Address',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const previousHomeAddress = define({
  label: 'Previous Home Address',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const mailingAddress = define({
  label: 'Mailing Address',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const workAddress = define({
  label: 'Current Work Address',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'text',
});
